import { Customer } from '@api/Trinium/Endpoints/Customer/types';
import { LegLocation } from '@api/Trinium/Endpoints/Leg';
import { Dayjs } from 'dayjs';
import * as Yup from 'yup';
import { AppLocalStorageKeys } from '@/Infrastructure/Configuration/types';

export interface ResearchFormProps {
  isFetching: boolean;
  rowCount: number;
}

export interface ResearchForm {
  isFetching: boolean;
  rowCount: number;
  onClose?: () => void;
}

/** Form field definitions for the Research Grid Toolbar */
export interface ResearchFormFields {
  jobDateRange: [Dayjs, Dayjs];
  order: string | null;
  customer: Customer | null;
  createdBy: string | null;
  container: string[] | null;
  masterBol: string | null;
  driver: string | null;
  customerRef: string | null;
  chassis: string | null;
  bookingRef: string | null;
  /** When true, only legs with approved charges. When false, only legs with unapproved charges. When null, ignored. */
  onlyApprovedCharges: 'All' | 'Only Unapproved' | 'Only Approved';
  delvDateRange: [Dayjs | null, Dayjs | null];
  schdPuDateRange: [Dayjs | null, Dayjs | null];
  schdDelvDateRange: [Dayjs | null, Dayjs | null];
  pickupLocation: LegLocation | null;
  deliveryLocation: LegLocation | null;
}

/** The kind of input control a filter field should render. */
export type ResearchFormFieldDataType = 'text' | 'dateRange' | 'customer' | 'driver' | 'chassis' | 'container' | 'user' | 'charges' | 'location';

/** Describes a selectable filter field: the Formik field it maps to, its dropdown label, and which input control renders its value. */
export interface ResearchFormFieldDefinition {
  fieldName: keyof ResearchFormFields;
  label: string;
  dataType: ResearchFormFieldDataType;
  placeholder?: string;
  /** When true, this field is always active and cannot be removed - it never appears in the "Add Filter" dropdown. */
  pinned?: boolean;
}

/** Available filter fields for the Research Grid Toolbar's "Add Filter" dropdown */
export const RESEARCH_FORM_FIELD_DEFINITIONS: ResearchFormFieldDefinition[] = [
  { fieldName: 'jobDateRange', label: 'Load Creation Date', dataType: 'dateRange', pinned: true },
  { fieldName: 'order', label: 'Order #', dataType: 'text', placeholder: '123456', pinned: true },
  { fieldName: 'customer', label: 'Customer', dataType: 'customer', pinned: true },
  { fieldName: 'container', label: 'Container #', dataType: 'container', placeholder: 'Enter Container #', pinned: true },
  { fieldName: 'masterBol', label: 'Master BOL', dataType: 'text', placeholder: 'Enter Master Bill of Lading' },
  { fieldName: 'driver', label: 'Driver/Broker Carrier', dataType: 'driver', pinned: true },
  { fieldName: 'customerRef', label: 'Customer Ref #', dataType: 'text', placeholder: 'Enter Customer Reference #', pinned: true },
  { fieldName: 'chassis', label: 'Chassis #', dataType: 'chassis', pinned: true },
  { fieldName: 'bookingRef', label: 'Booking Ref #', dataType: 'text', placeholder: 'Enter Booking Reference #' },
  { fieldName: 'createdBy', label: 'Created By', dataType: 'user' },
  { fieldName: 'onlyApprovedCharges', label: 'Charges', dataType: 'charges' },
  { fieldName: 'delvDateRange', label: 'Actual Delivery Date', dataType: 'dateRange', pinned: true },
  { fieldName: 'schdPuDateRange', label: 'Scheduled Pickup Date', dataType: 'dateRange' },
  { fieldName: 'schdDelvDateRange', label: 'Scheduled Delivery Date', dataType: 'dateRange' },
  { fieldName: 'pickupLocation', label: 'Pickup Location', dataType: 'location' },
  { fieldName: 'deliveryLocation', label: 'Delivery Location', dataType: 'location' }
];

/** Filter fields that always show up and cannot be removed from the Research Grid Toolbar */
export const RESEARCH_PINNED_FIELD_NAMES: ResearchFormFieldDefinition['fieldName'][] = RESEARCH_FORM_FIELD_DEFINITIONS.filter(
  (definition) => definition.pinned
).map((definition) => definition.fieldName);

/** Reads which optional (non-pinned) filter fields the user previously added, so they can be restored on the next visit. */
export function readPersistedFilterFieldNames(): ResearchFormFieldDefinition['fieldName'][] {
  const stored = localStorage.getItem(AppLocalStorageKeys.ResearchActiveFilterFields);
  if (!stored) return [];

  try {
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];

    const validFieldNames = new Set(RESEARCH_FORM_FIELD_DEFINITIONS.map((definition) => definition.fieldName));
    return (parsed as unknown[]).filter(
      (fieldName): fieldName is ResearchFormFieldDefinition['fieldName'] =>
        typeof fieldName === 'string' && validFieldNames.has(fieldName as ResearchFormFieldDefinition['fieldName'])
    );
  } catch {
    return [];
  }
}

/** Persists which optional (non-pinned) filter fields the user currently has added. */
export function persistFilterFieldNames(fieldNames: ResearchFormFieldDefinition['fieldName'][]): void {
  const persistable = fieldNames.filter((fieldName) => !RESEARCH_PINNED_FIELD_NAMES.includes(fieldName));
  localStorage.setItem(AppLocalStorageKeys.ResearchActiveFilterFields, JSON.stringify(persistable));
}

/**
 * Whether a filter's current value should be considered "unset". Does not apply to jobDateRange,
 * which always carries a populated default range - callers must special-case it.
 */
export function isResearchFilterValueEmpty(dataType: ResearchFormFieldDataType, value: unknown): boolean {
  switch (dataType) {
    case 'container':
      return !value || (Array.isArray(value) && value.length === 0);
    case 'charges':
      return value === 'All';
    case 'dateRange': {
      const [start, end] = (value as [unknown, unknown] | null) ?? [null, null];
      return !start && !end;
    }
    default:
      return !value;
  }
}

/**
 * The value a filter should reset to when its row is removed. Does not apply to jobDateRange,
 * which must always have a valid default range rather than nulls - callers must special-case it.
 */
export function getResearchFilterEmptyValue(dataType: ResearchFormFieldDataType): unknown {
  switch (dataType) {
    case 'customer':
    case 'container':
    case 'location':
      return null;
    case 'charges':
      return 'All';
    case 'dateRange':
      return [null, null];
    default:
      return '';
  }
}

/** Form validation schema for the Research Grid Toolbar */
export const researchValidationSchema = Yup.object<ResearchFormFields>()
  .shape({
    order: Yup.string()
      .nullable()
      .typeError('Please enter a valid Order Number.')
      .test('orderNumberLength', 'Please enter a 6 or 7-digit Order Number.', (value) => {
        return value ? value.length === 6 || value.length === 7 : true;
      }),
    customer: Yup.object().nullable(),
    createdBy: Yup.string().nullable(),
    container: Yup.array().of(Yup.string()).nullable(),
    masterBol: Yup.string().nullable().min(3, 'Please enter at least 3 characters for Master Bill of Lading.'),
    driver: Yup.string().nullable().min(3, 'Please enter at least 3 characters for Driver.'),
    customerRef: Yup.string().nullable().min(3, 'Please enter at least 3 characters for Customer Reference Number.'),
    chassis: Yup.string().nullable(),
    bookingRef: Yup.string().nullable().min(3, 'Please enter at least 3 characters for Booking Reference Number.'),
    jobDateRange: Yup.array().of(Yup.mixed<Dayjs>().required('Load Creation Date Range is required.')),
    onlyApprovedCharges: Yup.string(),
    delvDateRange: Yup.mixed<[Dayjs | null, Dayjs | null]>(),
    schdPuDateRange: Yup.mixed<[Dayjs | null, Dayjs | null]>(),
    schdDelvDateRange: Yup.mixed<[Dayjs | null, Dayjs | null]>(),
    pickupLocation: Yup.object().nullable(),
    deliveryLocation: Yup.object().nullable()
  })
  .test('atLeastOneField', 'Please enter at least one search criteria.', function (values) {
    // jobDateRange always carries a populated default range, so it never counts as a "real" search criteria
    const hasValue = RESEARCH_FORM_FIELD_DEFINITIONS.some(
      (definition) =>
        definition.fieldName !== 'jobDateRange' && !isResearchFilterValueEmpty(definition.dataType, values[definition.fieldName])
    );

    if (!hasValue) {
      return this.createError({
        // Path is required for the error to be associated with a specific field and displayed correctly in the form
        path: 'order',
        message: 'Please enter at least one search criteria .'
      });
    }

    return true;
  });
